import axios, { AxiosError, Method } from "axios";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "./useAuth";

interface FetchRequest<T> {
  method?: "get" | "post";
  url: string;
  params?: Params;
  body?: Params;
  headers?: Headers;
  skip?: boolean;
  onSuccess?: (data?: T) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onError?: (err: any) => void;
}

export type Refetch<T> = ({
  url,
  params,
}?: RefetchArgs<T>) => Promise<T | null>;

interface FetchResponse<T> {
  data?: T | null;
  refetch: Refetch<T>;
  error: AxiosError | null;
  hasError: boolean;
  isLoading: boolean;
}

interface Headers {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

interface Params {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

interface RefetchArgs<T> {
  url?: string;
  method?: Pick<Method, "get" & "post">;
  params?: Params;
  body?: Params;
  onSuccess?: (data?: T) => void;
  onError?: (err: AxiosError) => void;
}

export function useFetch<T>({
  url,
  method = "get",
  params,
  headers,
  body,
  skip,
  onError,
  onSuccess,
}: FetchRequest<T>): FetchResponse<T> {
  const [t] = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<AxiosError | null>(null);
  const [hasError, setHasError] = useState(false);

  const { authInfo } = useAuth();
  const memoizeUrl = useMemo(() => url, [url]);
  const refetch = useCallback<Refetch<T>>(
    // eslint-disable-next-line no-shadow
    async <T>(args?: RefetchArgs<T>) => {
      try {
        setLoading(true);

        await axios
          .request({
            method,
            url: memoizeUrl,
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              authorization: `Bearer ${authInfo.accessToken}`,
              ...headers,
            },
            params: {
              ...(args?.params || params),
            },
            data: {
              ...(args?.body || body),
            },
          })
          .then((res) => {
            // eslint-disable-next-line no-shadow
            const { data } = res;
            setData(data);

            if (onSuccess !== undefined) {
              onSuccess(data);
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return data;
          });
      } catch (err) {
        if (err.message !== "") {
          enqueueSnackbar(err.message, { variant: "error" });
        } else {
          enqueueSnackbar(t("general.service_failed"), { variant: "error" });
        }
        onError?.(err);
        setError(err as AxiosError);
        setHasError(true);
        return null;
      } finally {
        setLoading(false);
      }

      return null;
    },
    [headers, memoizeUrl, method, onError, params]
  );

  const clear = useCallback(() => {
    setData(null);
    setLoading(false);
    setHasError(false);
    setError(null);
  }, []);

  useEffect(() => {
    if (skip) return;
    if (memoizeUrl) {
      const f = async () => {
        const res = await refetch();
        // eslint-disable-next-line no-unused-expressions
        res && setData(res);
      };

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      f();
    }
    // eslint-disable-next-line consistent-return
    return () => clear();
  }, [memoizeUrl, skip]);

  return {
    data,
    refetch,
    error,
    hasError,
    isLoading,
  };
}
